import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Plus, Edit, Trash2, Search, X, Copy, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['hero', 'portfolio', 'blog', 'product', 'general'];

const AdminImages = () => {
  const { token } = useAuth();
  const { refreshStats } = useOutletContext();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    title_es: '',
    alt_text: '',
    alt_text_es: '',
    url: '',
    category: 'general',
    tags: ''
  });

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/images`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setImages(data);
      }
    } catch (error) {
      toast.error('Error loading images');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const submitData = {
      ...formData,
      tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(Boolean) : []
    };

    try {
      const url = editingImage 
        ? `${API_URL}/api/admin/images/${editingImage.id}`
        : `${API_URL}/api/admin/images`;
      
      const response = await fetch(url, {
        method: editingImage ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        toast.success(editingImage ? 'Image updated!' : 'Image added!');
        setShowModal(false);
        resetForm();
        fetchImages();
        refreshStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error saving image');
      }
    } catch (error) {
      toast.error('Error saving image');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/images/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Image deleted!');
        fetchImages();
        refreshStats();
      }
    } catch (error) {
      toast.error('Error deleting image');
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('URL copied to clipboard!');
  };

  const openEditModal = (image) => {
    setEditingImage(image);
    setFormData({
      title: image.title,
      title_es: image.title_es || '',
      alt_text: image.alt_text,
      alt_text_es: image.alt_text_es || '',
      url: image.url,
      category: image.category,
      tags: image.tags ? image.tags.join(', ') : ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingImage(null);
    setFormData({
      title: '',
      title_es: '',
      alt_text: '',
      alt_text_es: '',
      url: '',
      category: 'general',
      tags: ''
    });
  };

  const filteredImages = images.filter(image => {
    const matchesSearch = image.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          image.alt_text.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || image.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-terracota border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-images">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-serif text-white mb-1">Images</h1>
          <p className="text-stone-400 text-sm">{images.length} total images</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 bg-terracota text-white px-4 py-2 rounded-lg hover:bg-terracota/90 transition-colors"
          data-testid="add-image-btn"
        >
          <Plus className="w-5 h-5" />
          Add Image
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-500" />
          <input
            type="text"
            placeholder="Search images..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-stone-800 border border-stone-700 rounded-lg pl-10 pr-4 py-3 text-white placeholder:text-stone-500 focus:outline-none focus:border-terracota"
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="bg-stone-800 border border-stone-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-terracota"
        >
          <option value="">All Categories</option>
          {CATEGORIES.map(cat => (
            <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
          ))}
        </select>
      </div>

      {/* Images Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {filteredImages.length === 0 ? (
          <div className="col-span-full bg-stone-800 rounded-xl p-8 text-center border border-stone-700">
            <p className="text-stone-400">No images found</p>
          </div>
        ) : (
          filteredImages.map((image) => (
            <div 
              key={image.id}
              className="bg-stone-800 rounded-xl border border-stone-700 overflow-hidden group"
            >
              <div className="aspect-square relative overflow-hidden">
                <img 
                  src={image.url} 
                  alt={image.alt_text}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/300?text=Image+Error';
                  }}
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    onClick={() => copyUrl(image.url)}
                    className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                    title="Copy URL"
                  >
                    <Copy className="w-4 h-4 text-white" />
                  </button>
                  <a
                    href={image.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                    title="Open in new tab"
                  >
                    <ExternalLink className="w-4 h-4 text-white" />
                  </a>
                  <button
                    onClick={() => openEditModal(image)}
                    className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-4 h-4 text-white" />
                  </button>
                  <button
                    onClick={() => handleDelete(image.id)}
                    className="p-2 bg-red-500/50 rounded-lg hover:bg-red-500/70 transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>
              <div className="p-3">
                <p className="text-white text-sm font-medium truncate">{image.title}</p>
                <p className="text-stone-500 text-xs">{image.category}</p>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-stone-800 rounded-xl w-full max-w-2xl my-8 border border-stone-700">
            <div className="flex items-center justify-between p-4 border-b border-stone-700">
              <h2 className="text-lg font-semibold text-white">
                {editingImage ? 'Edit Image' : 'Add Image'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-stone-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-stone-400 text-sm mb-1">Image URL *</label>
                <input
                  type="url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                  placeholder="https://..."
                  required
                />
              </div>

              {formData.url && (
                <div className="rounded-lg overflow-hidden bg-stone-700 p-2">
                  <img 
                    src={formData.url} 
                    alt="Preview"
                    className="w-full h-32 object-contain rounded"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/300?text=Invalid+URL';
                    }}
                  />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-stone-400 text-sm mb-1">Title (EN) *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                    required
                  />
                </div>
                <div>
                  <label className="block text-stone-400 text-sm mb-1">Title (ES)</label>
                  <input
                    type="text"
                    value={formData.title_es}
                    onChange={(e) => setFormData({ ...formData, title_es: e.target.value })}
                    className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-stone-400 text-sm mb-1">Alt Text (EN) *</label>
                  <input
                    type="text"
                    value={formData.alt_text}
                    onChange={(e) => setFormData({ ...formData, alt_text: e.target.value })}
                    className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                    placeholder="Describe the image for accessibility"
                    required
                  />
                </div>
                <div>
                  <label className="block text-stone-400 text-sm mb-1">Alt Text (ES)</label>
                  <input
                    type="text"
                    value={formData.alt_text_es}
                    onChange={(e) => setFormData({ ...formData, alt_text_es: e.target.value })}
                    className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-stone-400 text-sm mb-1">Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                    required
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-stone-400 text-sm mb-1">Tags (comma-separated)</label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    className="w-full bg-stone-700 border border-stone-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-terracota"
                    placeholder="events, portfolio, featured"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-stone-700">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-stone-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-terracota text-white rounded-lg hover:bg-terracota/90 transition-colors"
                >
                  {editingImage ? 'Update Image' : 'Add Image'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminImages;
